import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page PayoutPage
 * @description 獎金發放（UI-2-7）
 * 顯示獎金分配預覽、ICM 試算（限時賽）、手動調整、確認發放
 */

import { BrandIcon } from '../../components/layout/BrandIcon';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TournamentDto, PlayerEntryDto, IcmInputDto } from '@pokerroom/types';
import { isAxiosError } from 'axios';
import { api } from '../../lib/api.ts';
import { useSiteName } from '../../hooks/useSiteName';
import { useAuthStore } from '../../store/auth.store';
import type { PayoutRule } from './payoutRule.ts';

interface RebuyAddonLog {
  id: string;
  entryId: string;
  type: 'REBUY' | 'ADDON' | 'REENTRY';
  isReverted?: boolean;
  chipsAdded: number;
}

async function fetchTournament(id: string): Promise<TournamentDto & { entries: PlayerEntryDto[] }> {
  return api.get<TournamentDto & { entries: PlayerEntryDto[] }>(`/tournament/${id}`);
}

async function fetchRebuyAddons(id: string): Promise<RebuyAddonLog[]> {
  return api.get<RebuyAddonLog[]>(`/tournament/${id}/rebuy-addons`);
}

async function calcIcm(dto: IcmInputDto): Promise<{ equities: number[] }> {
  return api.post<{ equities: number[] }>('/tournament/icm/calculate', dto);
}

async function completeTournament(
  id: string,
  payouts?: Array<{ entryId: string; prizeAmount: number }>,
  adjustmentNote?: string,
) {
  return api.post(
    `/tournament/${id}/complete`,
    payouts === undefined ? {} : { payouts, adjustmentNote },
  );
}

/**
 * Round to nearest 100, then adjust so total === target.
 * Remainder is added to the largest entry (1st place).
 */
function roundToHundredAndBalance(amounts: number[], target: number): number[] {
  const rounded = amounts.map((v) => Math.round(v / 100) * 100);
  const diff = target - rounded.reduce((a, b) => a + b, 0);
  if (diff !== 0 && rounded.length > 0) {
    // Add remainder to the largest (first) entry
    rounded[0] = rounded[0]! + diff;
  }
  return rounded;
}

/** Status priority for sorting (lower = higher priority) */
function statusOrder(status: string): number {
  switch (status) {
    case 'PLAYING':
    case 'FINAL_TABLE':
    case 'FINISHED':
      return 0;
    case 'ELIMINATED':
      return 1;
    default:
      return 2;
  }
}

/** Standard payout percentages by entry count (matches backend) */
function getStandardPayoutPercentages(entryCount: number): number[] {
  if (entryCount <= 2) return [100];
  if (entryCount <= 6) return [65, 35];
  if (entryCount <= 10) return [50, 30, 20];
  if (entryCount <= 18) return [40, 30, 20, 10];
  if (entryCount <= 27) return [35, 25, 18, 12, 10];
  if (entryCount <= 36) return [33, 23, 16, 12, 9, 7];
  if (entryCount <= 54) return [30, 20, 15, 11, 8, 6, 5, 5];
  return [28, 18, 13, 10, 8, 6, 5, 4, 4, 4];
}

function roundedRuleAward(
  pool: number,
  poolPercent: number,
  placePercent: number,
  unit: number,
): number {
  const cents = BigInt(Math.round(pool * 100));
  const unitCents = BigInt(unit * 100);
  const rounded =
    ((cents * BigInt(poolPercent) * BigInt(placePercent)) / (10000n * unitCents)) * unitCents;
  return Number(rounded) / 100;
}

export default function PayoutPage() {
  const { t } = useTranslation('tournament');
  const { id: tournamentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const siteName = useSiteName();
  const qc = useQueryClient();
  const canAdjust = useAuthStore((s) => s.allowedActions?.includes('tournament:complete') ?? false);
  const [allocationMode, setAllocationMode] = useState<'AUTOMATIC' | 'ADJUSTED'>('AUTOMATIC');
  const [adjustmentNote, setAdjustmentNote] = useState('');
  const printRef = useRef<HTMLDivElement>(null);

  const [icmChips, setIcmChips] = useState<Record<string, number>>({});
  const [icmResult, setIcmResult] = useState<number[] | null>(null);
  // Editable payout amounts: entryId → prize amount
  const [editPayouts, setEditPayouts] = useState<Record<string, number>>({});
  const [payoutsConfirmed, setPayoutsConfirmed] = useState(false);
  const [sortByPrize, setSortByPrize] = useState(false);

  const { data: tournament } = useQuery({
    queryKey: ['tournament', tournamentId],
    queryFn: () => fetchTournament(tournamentId!),
    enabled: !!tournamentId,
  });

  const { data: rebuyAddons = [] } = useQuery({
    queryKey: ['rebuy-addons', tournamentId],
    queryFn: () => fetchRebuyAddons(tournamentId!),
    enabled: !!tournamentId,
  });

  const [mutationError, setMutationError] = useState<string | null>(null);

  const icmMutation = useMutation({
    mutationFn: calcIcm,
    onError: (err) => {
      setMutationError(err instanceof Error ? err.message : 'ICM calculation failed');
    },
    onSuccess: (data) => {
      setIcmResult(data.equities);
      // Auto-apply ICM results to payout
      const rawAmounts = data.equities.map((v) => v ?? 0);
      const entries_ = tournament?.entries ?? [];
      const playing_ = entries_.filter(
        (e) => e.status === 'PLAYING' || e.status === 'FINAL_TABLE' || e.status === 'CHECKED_IN',
      );
      const pool = Number(tournament?.prizePool ?? 0);
      const rounded = roundToHundredAndBalance(rawAmounts, pool);
      const newPayouts: Record<string, number> = {};
      playing_.forEach((entry, i) => {
        newPayouts[entry.id] = rounded[i]!;
      });
      entries_
        .filter((e) => e.status === 'ELIMINATED')
        .forEach((e) => {
          newPayouts[e.id] = 0;
        });
      setEditPayouts(newPayouts);
      setAllocationMode('ADJUSTED');
      setPayoutsConfirmed(false);
      setSortByPrize(true);
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => {
      const payouts = Object.entries(editPayouts)
        .filter(([, amount]) => amount > 0)
        .map(([entryId, prizeAmount]) => ({ entryId, prizeAmount }));
      return completeTournament(
        tournamentId!,
        allocationMode === 'AUTOMATIC' ? undefined : payouts,
        allocationMode === 'ADJUSTED' ? adjustmentNote.trim() : undefined,
      );
    },
    onError: (err) => {
      setMutationError(
        isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : err instanceof Error
            ? err.message
            : 'Failed to complete tournament',
      );
    },
    onSuccess: () => {
      setMutationError(null);
      void qc.invalidateQueries({ queryKey: ['tournament', tournamentId] });
    },
  });

  const entries = tournament?.entries ?? [];
  const playing = entries.filter(
    (e) => e.status === 'PLAYING' || e.status === 'FINAL_TABLE' || e.status === 'CHECKED_IN',
  );
  const prizePool = Number(tournament?.prizePool ?? 0);
  const isCompleted = tournament?.status === 'COMPLETED';
  const isTimedTournament =
    tournament?.type === 'TIMED' ||
    (tournament?.type !== 'SPECIAL' && tournament?.payoutType === 'ICM');

  // All entries that participated
  const allParticipants = entries
    .filter((e) =>
      ['PLAYING', 'FINAL_TABLE', 'FINISHED', 'ELIMINATED', 'CHECKED_IN'].includes(e.status),
    )
    .sort((a, b) => {
      const ra = a.finalRank ?? 9999;
      const rb = b.finalRank ?? 9999;
      if (ra !== rb) return ra - rb;
      return statusOrder(a.status) - statusOrder(b.status);
    });

  // Per-entry rebuy/addon counts
  const entryRebuyCount: Record<string, number> = {};
  const entryAddonCount: Record<string, number> = {};
  const entryReentryCount: Record<string, number> = {};
  for (const log of rebuyAddons) {
    if (log.isReverted) continue;
    if (log.type === 'REENTRY')
      entryReentryCount[log.entryId] = (entryReentryCount[log.entryId] ?? 0) + 1;
    else if (log.type === 'REBUY')
      entryRebuyCount[log.entryId] = (entryRebuyCount[log.entryId] ?? 0) + 1;
    else entryAddonCount[log.entryId] = (entryAddonCount[log.entryId] ?? 0) + 1;
  }
  const totalRebuys = rebuyAddons.filter((l) => l.type === 'REBUY' && !l.isReverted).length;
  const totalAddons = rebuyAddons.filter((l) => l.type === 'ADDON' && !l.isReverted).length;

  const reentryChips = rebuyAddons
    .filter((log) => log.type === 'REENTRY' && !log.isReverted)
    .reduce((sum, log) => sum + log.chipsAdded, 0);

  // Chip verification — compare expected total chips vs admin-entered ICM chip counts
  const totalParticipants = allParticipants.length;
  const rankTierEntrants = entries.filter(
    (e) => e.status !== 'CANCELLED' && e.status !== 'NO_SHOW',
  ).length;
  const startingChips = tournament?.startingChips ?? 0;
  const rebuyChips = tournament?.rebuyChips ?? 0;
  const addonChips = tournament?.addonChips ?? 0;
  const expectedChips =
    totalParticipants * startingChips +
    totalRebuys * rebuyChips +
    totalAddons * addonChips +
    reentryChips;
  // Use ICM input values for remaining players (eliminated players' chips are now held by remaining)
  const actualChips = playing.reduce((sum, e) => sum + (icmChips[e.id] ?? e.currentChips), 0);
  const chipsMatch = expectedChips === actualChips;

  // --- Payout calculation helpers ---

  /** Build ranked list: remaining players sorted by chips (rank 1, 2, ...) + eliminated with finalRank */
  const rankedForPayout = useMemo(() => {
    // Remaining players sorted by chips desc
    const remaining = [...playing].sort(
      (a, b) => b.currentChips - a.currentChips || a.id.localeCompare(b.id),
    );
    const result: Array<{ entry: PlayerEntryDto; rank: number }> = [];
    remaining.forEach((e, i) => result.push({ entry: e, rank: i + 1 }));
    // Eliminated players with existing ranks
    const eliminated = entries
      .filter((e) => e.status === 'ELIMINATED' && e.finalRank != null)
      .sort((a, b) => (a.finalRank ?? 999) - (b.finalRank ?? 999));
    eliminated.forEach((e) => result.push({ entry: e, rank: e.finalRank! }));
    return result.sort((a, b) => a.rank - b.rank);
  }, [playing, entries]);

  /** Preview the frozen server rule. Each component rounds down and residual stays unallocated. */
  const autoCalculateStandard = () => {
    const rule: PayoutRule = tournament?.payoutRuleSnapshot?.rule ?? {
      pool: { primaryPercent: 100, rankBonusPercent: 0 },
      rankTiers: [{ maxEntrants: null, shares: getStandardPayoutPercentages(rankTierEntrants) }],
      roundingUnit: 1,
    };
    const pcts =
      rule.rankTiers.find(
        (tier) => tier.maxEntrants === null || rankTierEntrants <= tier.maxEntrants,
      )?.shares ?? [];
    const newPayouts: Record<string, number> = {};
    for (const { entry, rank } of rankedForPayout) {
      const percent = pcts[rank - 1] ?? 0;
      newPayouts[entry.id] =
        roundedRuleAward(prizePool, rule.pool.primaryPercent, percent, rule.roundingUnit) +
        roundedRuleAward(prizePool, rule.pool.rankBonusPercent, percent, rule.roundingUnit);
    }
    setEditPayouts(newPayouts);
    setAllocationMode('AUTOMATIC');
    setAdjustmentNote('');
    setPayoutsConfirmed(false);
  };

  /** Apply ICM (Malmuth-Harville) results to payouts (rounded to 100, total = prizePool) */
  const applyIcmResults = () => {
    if (!icmResult) return;
    const rawAmounts = playing.map((_, i) => icmResult[i] ?? 0);
    const rounded = roundToHundredAndBalance(rawAmounts, prizePool);
    const newPayouts: Record<string, number> = {};
    playing.forEach((entry, i) => {
      newPayouts[entry.id] = rounded[i]!;
    });
    // Eliminated get 0 in ICM mode
    entries
      .filter((e) => e.status === 'ELIMINATED')
      .forEach((e) => {
        newPayouts[e.id] = 0;
      });
    setEditPayouts(newPayouts);
    setAllocationMode('ADJUSTED');
    setPayoutsConfirmed(false);
  };

  /** Calculate chip chop: proportional to chip counts (rounded to 100, total = prizePool) */
  const calculateChipChop = () => {
    const totalChips = playing.reduce((sum, e) => sum + (icmChips[e.id] ?? e.currentChips), 0);
    const newPayouts: Record<string, number> = {};
    if (totalChips > 0) {
      // Sort by chips desc for rounding — 1st place gets remainder
      const sorted = [...playing].sort(
        (a, b) => (icmChips[b.id] ?? b.currentChips) - (icmChips[a.id] ?? a.currentChips),
      );
      const rawAmounts = sorted.map((entry) => {
        const chips = icmChips[entry.id] ?? entry.currentChips;
        return (chips / totalChips) * prizePool;
      });
      const rounded = roundToHundredAndBalance(rawAmounts, prizePool);
      sorted.forEach((entry, i) => {
        newPayouts[entry.id] = rounded[i]!;
      });
    }
    // Eliminated get 0 in ICM mode
    entries
      .filter((e) => e.status === 'ELIMINATED')
      .forEach((e) => {
        newPayouts[e.id] = 0;
      });
    setEditPayouts(newPayouts);
    setAllocationMode('ADJUSTED');
    setPayoutsConfirmed(false);
  };

  // Auto-initialize payouts on first load (if not completed)
  const payoutsInitializedRef = useRef(false);
  useEffect(() => {
    if (payoutsInitializedRef.current) return;
    if (isCompleted || rankedForPayout.length === 0) return;
    payoutsInitializedRef.current = true;
    if (isTimedTournament && playing.length > 0) {
      calculateChipChop();
    } else {
      autoCalculateStandard();
    }
  }, [isCompleted, rankedForPayout.length, isTimedTournament, playing.length, prizePool]);

  if (!tournament)
    return (
      <AdminPage header={<AdminPageHeader title={t('payout.title')} />}>
        {' '}
        <div className="p-6 text-base-content/70">{t('payout.loading')}</div>{' '}
      </AdminPage>
    );

  const validPayoutAmounts = Object.values(editPayouts).every(
    (v) =>
      Number.isFinite(v) && v >= 0 && v <= 9999999999.99 && /^\d+(?:\.\d{1,2})?$/.test(String(v)),
  );
  const totalCents = Object.values(editPayouts).reduce((sum, v) => sum + Math.round(v * 100), 0);
  const totalPayout = totalCents / 100;
  const payoutDiff = (Math.round(prizePool * 100) - totalCents) / 100;

  /** ICM Malmuth-Harville: calculate and auto-apply results */
  const handleIcmCalcAndApply = () => {
    const chipCounts = playing.map((e) => icmChips[e.id] ?? e.currentChips);
    // Build payout structure for N remaining players where ALL positions share the pool
    const n = playing.length;
    const ratios: number[] = [];
    if (n === 1) {
      ratios.push(1.0);
    } else {
      // Top-heavy structure: 1st gets most, decreasing, all sum to 1.0
      // Using standard percentages scaled to cover all N positions
      const pcts = getStandardPayoutPercentages(n);
      const pctSum = pcts.reduce((a, b) => a + b, 0);
      for (const p of pcts) ratios.push(p / pctSum);
      // Pad if pcts has fewer entries than N
      while (ratios.length < n) ratios.push(0);
    }
    icmMutation.mutate({ chipCounts, payoutRatios: ratios, totalPrizePool: prizePool });
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const w = window.open('', '_blank', 'width=1100,height=700');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>${t('payout.prizeSignOffSheet')}</title>
      <style>
        @page { size: A4 landscape; margin: 15mm; }
        body { font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif; padding: 20px 30px; }
        h1 { font-size: 20px; text-align: center; margin-bottom: 4px; }
        h2 { font-size: 13px; text-align: center; color: #666; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #333; padding: 8px 12px; text-align: left; font-size: 14px; }
        th { background: #e8e8e8; font-weight: bold; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .prize { font-weight: bold; font-size: 15px; }
        .sign-col { width: 140px; }
        .status { color: #666; font-size: 12px; }
        .footer { margin-top: 24px; font-size: 11px; color: #888; text-align: center; }
      </style></head><body>`);
    w.document.write(printContent.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.print();
  };

  const statusLabel = (entry: PlayerEntryDto) => {
    const prize = Number(entry.prizeAmount);
    if (prize > 0) return `NT$ ${prize.toLocaleString()}`;
    return t(`playerStatus.${entry.status}`);
  };

  const statusColor = (entry: PlayerEntryDto) => {
    const prize = Number(entry.prizeAmount);
    if (prize > 0) return 'text-warning font-medium';
    if (entry.status === 'ELIMINATED') return 'text-error/70';
    if (entry.status === 'FINISHED') return 'text-success/70';
    return 'text-white/50';
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return (
    <AdminPage
      contentClassName="admin-content-short"
      header={
        <AdminPageHeader
          className="print:hidden"
          title={t('payout.title')}
          back={
            <button
              onClick={() => navigate(-1)}
              className="text-base-content/70 hover:text-base-content/70 inline-flex min-h-11 items-center text-sm print:hidden"
            >
              {t('payout.back')}
            </button>
          }
        />
      }
    >
      {/* 賽事已結算提示 */}
      {isCompleted && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-6 text-center">
          <span className="text-success font-medium">{t('payout.tournamentCompleted')}</span>
        </div>
      )}

      {/* Error display */}
      {mutationError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 flex items-center justify-between">
          <span className="text-error text-sm">{mutationError}</span>
          <button
            onClick={() => setMutationError(null)}
            className="text-error/50 hover:text-error text-sm ml-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* 獎金池 */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 text-center print:hidden">
        <div className="text-4xl font-bold text-warning">NT$ {prizePool.toLocaleString()}</div>
        <div className="text-warning/70 text-sm mt-1">{t('payout.totalPrizePool')}</div>
        {Number(tournament.overlayAmount) > 0 && (
          <div className="text-orange-300 text-sm mt-2">
            {t('payout.overlay', { amount: tournament.overlayAmount })}
          </div>
        )}
        {/* Payout type indicator */}
        <div className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold bg-base-300 text-base-content/70">
          {t('payoutType.' + tournament.payoutType)}
        </div>
      </div>

      {/* 獎金結構（結算前顯示） */}
      {!isCompleted && totalParticipants > 0 && (
        <div className="bg-base-300 border border-base-300 rounded-xl p-4 mb-6 print:hidden">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base-content font-medium">{t('prizeStructure')}</h2>
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-bold ${isTimedTournament ? 'bg-blue-500/20 text-info border border-blue-500/30' : 'bg-emerald-500/20 text-success border border-emerald-500/30'}`}
            >
              {isTimedTournament ? t('icmDistribution') : t('standardDistribution')}
            </span>
          </div>
          {isTimedTournament ? (
            <>
              <p className="text-base-content/70 text-sm mb-2">{t('icmDescription')}</p>
              <div className="bg-base-300 rounded-lg p-3 text-sm">
                <div className="flex justify-between text-base-content/70 mb-1">
                  <span>{t('remainingPlayers')}</span>
                  <span>{t('playerCountUnit', { count: playing.length })}</span>
                </div>
                <div className="flex justify-between text-base-content/70">
                  <span>{t('distributePrizePool')}</span>
                  <span className="text-warning">NT$ {prizePool.toLocaleString()} (100%)</span>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-1 text-sm">
              {(() => {
                const pcts = getStandardPayoutPercentages(rankTierEntrants);
                const rawAmounts = pcts.map((p) => (prizePool * p) / 100);
                const rounded = roundToHundredAndBalance(rawAmounts, prizePool);
                return pcts.map((pct, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5 rounded">
                    <span className="text-base-content/70">#{i + 1}</span>
                    <span className="text-base-content/70">{pct}%</span>
                    <span className="text-warning/80">NT$ {rounded[i]!.toLocaleString()}</span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* ICM 籌碼輸入（限時賽，未結算，剩餘 ≥ 2 人） */}
      {isTimedTournament && playing.length > 0 && !isCompleted && (
        <div className="bg-base-300 border border-base-300 rounded-xl p-4 mb-6 print:hidden">
          <h2 className="text-base-content font-medium mb-3">{t('enterPlayerChips')}</h2>
          <div className="space-y-2 mb-4">
            {playing.map((entry) => {
              const chips = icmChips[entry.id] ?? entry.currentChips;
              const chipPct = actualChips > 0 ? ((chips / actualChips) * 100).toFixed(1) : '0.0';
              return (
                <div key={entry.id} className="flex items-center gap-3">
                  <span className="text-base-content text-sm w-24 truncate">
                    {entry.playerName}
                  </span>
                  <input
                    type="number"
                    value={chips}
                    onChange={(e) =>
                      setIcmChips((prev) => ({
                        ...prev,
                        [entry.id]: parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    className="flex-1 bg-base-300 text-base-content rounded-lg px-3 py-2 text-sm border border-base-300"
                    placeholder={t('payout.chipCount')}
                  />
                  <span className="text-base-content/70 text-xs w-14 text-right">{chipPct}%</span>
                </div>
              );
            })}
          </div>
          <button
            onClick={handleIcmCalcAndApply}
            disabled={icmMutation.isPending}
            className="w-full py-2 bg-yellow-600/20 text-warning rounded-lg border border-yellow-600/40 hover:bg-yellow-600/30 text-sm font-medium"
          >
            {icmMutation.isPending ? t('payout.calculating') : t('calculatePrize')}
          </button>
          {icmResult && (
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <span className="text-info text-sm font-medium">{t('icmResultTitle')}</span>
              <div className="mt-2">
                {playing.map((entry, i) => (
                  <div key={entry.id} className="flex justify-between text-sm py-0.5">
                    <span className="text-base-content/70">{entry.playerName}</span>
                    <span className="text-warning">
                      NT$ {Math.floor(icmResult[i] ?? 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 籌碼核對（僅 ICM 賽事，管理員輸入籌碼後才有意義） */}
      {!isCompleted && isTimedTournament && playing.length > 0 && (
        <div
          className={`border rounded-xl p-4 mb-6 print:hidden ${chipsMatch ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
        >
          <h2 className="text-base-content font-medium mb-3">{t('payout.chipVerification')}</h2>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-base-content/70">{t('payout.expectedChips')}</span>
            <span className="text-base-content text-right">{expectedChips.toLocaleString()}</span>
            <span className="text-base-content/70 text-xs pl-2">
              {t('payout.initialChips', {
                count: totalParticipants,
                chips: startingChips.toLocaleString(),
              })}
            </span>
            <span className="text-base-content/70 text-xs text-right">
              {(totalParticipants * startingChips).toLocaleString()}
            </span>
            {reentryChips > 0 && (
              <>
                <span className="text-base-content/70 text-xs pl-2">{t('cashier.REENTRY')}</span>
                <span className="text-base-content/70 text-xs text-right">
                  {reentryChips.toLocaleString()}
                </span>
              </>
            )}
            {totalRebuys > 0 && (
              <>
                <span className="text-base-content/70 text-xs pl-2">
                  {t('payout.rebuyChips', {
                    count: totalRebuys,
                    chips: rebuyChips.toLocaleString(),
                  })}
                </span>
                <span className="text-base-content/70 text-xs text-right">
                  {(totalRebuys * rebuyChips).toLocaleString()}
                </span>
              </>
            )}
            {totalAddons > 0 && (
              <>
                <span className="text-base-content/70 text-xs pl-2">
                  {t('payout.addonChipsDetail', {
                    count: totalAddons,
                    chips: addonChips.toLocaleString(),
                  })}
                </span>
                <span className="text-base-content/70 text-xs text-right">
                  {(totalAddons * addonChips).toLocaleString()}
                </span>
              </>
            )}
            <span className="text-base-content/70">{t('payout.actualChips')}</span>
            <span className="text-base-content text-right">{actualChips.toLocaleString()}</span>
          </div>
          <div className={`mt-3 text-sm font-medium ${chipsMatch ? 'text-success' : 'text-error'}`}>
            {chipsMatch
              ? t('payout.chipsMatch')
              : t('payout.chipsMismatch', { diff: (expectedChips - actualChips).toLocaleString() })}
          </div>
        </div>
      )}

      {/* 獎金分配預覽（未結算時可編輯） */}
      {!isCompleted && rankedForPayout.length > 0 && (
        <div className="bg-base-300 border border-base-300 rounded-xl mb-6 print:hidden">
          <div className="p-4 border-b border-base-300 flex items-center justify-between">
            <h2 className="text-base-content font-medium">{t('payoutPreview')}</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setSortByPrize((v) => !v)}
                className={`text-xs px-3 py-1 rounded ${sortByPrize ? 'bg-yellow-500/20 text-warning border border-yellow-500/30' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
              >
                {t('sortByPrize')}
              </button>
              <button
                onClick={autoCalculateStandard}
                disabled={!canAdjust}
                className="text-xs px-3 py-1 rounded bg-base-300 text-base-content/70 hover:bg-base-300"
              >
                {t('standardPercentage')}
              </button>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {(sortByPrize
              ? [...rankedForPayout].sort(
                  (a, b) => (editPayouts[b.entry.id] ?? 0) - (editPayouts[a.entry.id] ?? 0),
                )
              : rankedForPayout
            ).map(({ entry, rank }) => {
              const rebuys = entryRebuyCount[entry.id] ?? 0;
              const addons = entryAddonCount[entry.id] ?? 0;
              const reentries = entryReentryCount[entry.id] ?? 0;
              const totalBuyins = 1 + reentries + rebuys + addons;
              const prize = editPayouts[entry.id] ?? 0;
              return (
                <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="text-warning/80 text-sm w-8 font-bold">#{rank}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-base-content">{entry.playerName}</span>
                    <span className="text-base-content/70 text-xs ml-2">{totalBuyins}x</span>
                    {entry.status === 'ELIMINATED' && (
                      <span className="text-error/50 text-xs ml-2">
                        ({t('playerStatus.ELIMINATED')})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-base-content/70 text-sm">NT$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={!canAdjust}
                      aria-label={`${t('payout.awardFor')} ${entry.playerName}`}
                      value={prize}
                      onChange={(e) => {
                        setEditPayouts((prev) => ({
                          ...prev,
                          [entry.id]: Number(e.target.value),
                        }));
                        setAllocationMode('ADJUSTED');
                        setPayoutsConfirmed(false);
                      }}
                      className="w-24 bg-base-300 text-warning text-right rounded px-2 py-1 text-sm border border-base-300 focus:border-yellow-500 outline-hidden"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Total & diff */}
          <div className="p-4 border-t border-base-300 flex items-center justify-between">
            <span className="text-base-content/70 text-sm">{t('totalPayout')}</span>
            <div className="text-right">
              <span className={`font-bold ${payoutDiff === 0 ? 'text-success' : 'text-error'}`}>
                NT$ {totalPayout.toLocaleString()}
              </span>
              {payoutDiff !== 0 && (
                <span className="text-error/70 text-xs ml-2">
                  (
                  {payoutDiff > 0
                    ? t('remaining', { amount: payoutDiff.toLocaleString() })
                    : t('exceeded', { amount: Math.abs(payoutDiff).toLocaleString() })}
                  )
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {!isCompleted && (
        <div className="mb-4">
          <p>
            {t(allocationMode === 'AUTOMATIC' ? 'payout.automaticMode' : 'payout.adjustedMode')}
          </p>
          {allocationMode === 'AUTOMATIC' && payoutDiff >= 0 && (
            <p>{t('payout.residual', { amount: payoutDiff.toLocaleString() })}</p>
          )}
          {allocationMode === 'ADJUSTED' && (
            <label>
              {t('payout.adjustmentNote')}
              <textarea
                aria-label={t('payout.adjustmentNote')}
                maxLength={500}
                value={adjustmentNote}
                disabled={!canAdjust}
                onChange={(e) => setAdjustmentNote(e.target.value)}
                className="block w-full bg-base-300"
              />
            </label>
          )}
          {tournament.cashSettlementState !== 'UNSETTLED' && (
            <p role="alert">{t('payout.historyUnverified')}</p>
          )}
        </div>
      )}

      {/* 已結算：所有玩家狀態 */}
      {isCompleted && allParticipants.length > 0 && (
        <div className="bg-base-300 border border-base-300 rounded-xl mb-6 print:hidden">
          <div className="p-4 border-b border-base-300">
            <h2 className="text-base-content font-medium">{t('payout.allPlayerStatus')}</h2>
          </div>
          <div className="divide-y divide-white/5">
            {allParticipants.map((entry) => {
              const rebuys = entryRebuyCount[entry.id] ?? 0;
              const addons = entryAddonCount[entry.id] ?? 0;
              const reentries = entryReentryCount[entry.id] ?? 0;
              const totalBuyins = 1 + reentries + rebuys + addons;
              return (
                <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-base-content/70 text-sm w-8">
                      {entry.finalRank ? `#${entry.finalRank}` : '—'}
                    </span>
                    <div>
                      <span className="text-base-content">{entry.playerName}</span>
                      <span className="text-base-content/70 text-xs ml-2">
                        {totalBuyins > 1
                          ? `${totalBuyins}x (${[reentries ? `E${reentries}` : '', rebuys ? `R${rebuys}` : '', addons ? `A${addons}` : ''].filter(Boolean).join('+')})`
                          : '1x'}
                      </span>
                    </div>
                  </div>
                  <span className={statusColor(entry)}>{statusLabel(entry)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 操作按鈕 */}
      <div className="flex gap-3 print:hidden">
        {/* 列印獎金簽收單 */}
        {isCompleted && allParticipants.length > 0 && (
          <>
            <button
              onClick={handlePrint}
              className="flex-1 py-4 bg-base-300 text-base-content rounded-xl font-bold text-lg hover:bg-base-300 border border-base-300 flex items-center justify-center gap-2"
            >
              <BrandIcon value="print" size={24} />
              {t('payout.printSignOff')}
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.get<Blob>(
                    `/tournament/${tournamentId}/payout-receipts/pdf`,
                    { responseType: 'blob' as never },
                  );
                  const blob =
                    res instanceof Blob
                      ? res
                      : new Blob([res as never], { type: 'application/pdf' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `receipts-${tournamentId?.slice(0, 8)}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  /* interceptor handles */
                }
              }}
              className="py-4 px-6 bg-base-300 text-base-content rounded-xl font-bold text-lg hover:bg-base-300 border border-base-300 flex items-center justify-center gap-2"
            >
              <BrandIcon value="receipt_long" size={24} />
              PDF
            </button>
          </>
        )}

        {/* 結束賽事 */}
        {isCompleted ? (
          <div className="flex-1 py-4 bg-green-600/20 text-success rounded-xl font-bold text-lg text-center border border-green-600/30 cursor-default">
            {t('payout.tournamentCompleted')}
          </div>
        ) : (
          (() => {
            const chipCheckOk = !isTimedTournament || chipsMatch;
            const payoutOk =
              validPayoutAmounts &&
              (allocationMode === 'AUTOMATIC' ? payoutDiff >= 0 : payoutDiff === 0);
            const noteOk = allocationMode === 'AUTOMATIC' || !!adjustmentNote.trim();
            const historyOk = tournament.cashSettlementState === 'UNSETTLED';
            const canComplete =
              canAdjust &&
              historyOk &&
              chipCheckOk &&
              payoutOk &&
              noteOk &&
              !completeMutation.isPending;
            return (
              <div className="flex-1">
                <button
                  onClick={() => {
                    if (confirm(t('payout.confirmEnd'))) completeMutation.mutate();
                  }}
                  disabled={!canComplete}
                  className="w-full py-4 bg-primary text-primary-content rounded-xl font-bold text-lg hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {completeMutation.isPending
                    ? t('payout.completing')
                    : t('payout.completeTournament')}
                </button>
                {!canComplete && !completeMutation.isPending && (
                  <div className="mt-2 text-xs text-error/80 text-center space-y-0.5">
                    {!chipCheckOk && <div>{t('chipVerificationFailed')}</div>}
                    {!payoutOk && (
                      <div>
                        {t('payoutMismatch', { diff: Math.abs(payoutDiff).toLocaleString() })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* 隱藏的列印用簽收單 */}
      <div ref={printRef} className="hidden">
        <h1>
          {siteName} — {t('payout.prizeSignOffSheet')}
        </h1>
        <h2>
          {tournament.name} | {dateStr} | {t('payout.totalPrizePool')}: NT${' '}
          {prizePool.toLocaleString()}
        </h2>
        <table>
          <thead>
            <tr>
              <th className="text-center" style={{ width: 60 }}>
                {t('payout.rank')}
              </th>
              <th>{t('payout.playerName')}</th>
              <th className="text-center" style={{ width: 80 }}>
                {t('payout.buyinCount')}
              </th>
              <th className="text-center" style={{ width: 70 }}>
                Rebuy
              </th>
              <th className="text-center" style={{ width: 70 }}>
                Addon
              </th>
              <th className="text-right" style={{ width: 140 }}>
                {t('payout.prizeAmountLabel')}
              </th>
              <th className="text-center" style={{ width: 100 }}>
                {t(`playerStatus.ELIMINATED`)}
              </th>
              <th className="sign-col text-center">{t('payout.signature')}</th>
            </tr>
          </thead>
          <tbody>
            {allParticipants.map((entry) => {
              const rebuys = entryRebuyCount[entry.id] ?? 0;
              const addons = entryAddonCount[entry.id] ?? 0;
              const prize = Number(entry.prizeAmount);
              return (
                <tr key={entry.id}>
                  <td className="text-center">{entry.finalRank ? `#${entry.finalRank}` : '—'}</td>
                  <td>{entry.playerName}</td>
                  <td className="text-center">{1 + rebuys + addons}x</td>
                  <td className="text-center">{rebuys > 0 ? rebuys : '—'}</td>
                  <td className="text-center">{addons > 0 ? addons : '—'}</td>
                  <td className="text-right prize">
                    {prize > 0 ? `NT$ ${prize.toLocaleString()}` : '—'}
                  </td>
                  <td className="text-center status">{t(`playerStatus.${entry.status}`)}</td>
                  <td className="sign-col">{prize > 0 ? '' : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="footer">{t('payout.signOffNote')}</div>
      </div>
    </AdminPage>
  );
}
